import { rootApi } from "../../api/rootApi";

export const rulesApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getRules: builder.mutation({
      query: (params) => ({
        method: "GET",
        url: `/tenant/rules?limit=${params.limit}&offset=${params.offset}${params.status ? `&status=${params.status}` : ""
          }${params.rule_type ? `&rule_type=${params.rule_type}` : ""}${params.name ? `&name=${params.name}` : ""
          }${params.event_type ? `&event_type=${params.event_type}` : ""}`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + params.token,
          slug: params.tenant_id,
        },
      }),
    }),
    getRuleById: builder.mutation({
      query: (params) => ({
        method: "GET",
        url: `/tenant/rules/${params.id}`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + params.token,
          slug: params.tenant_id,
        },
      }),
    }),
    createRule: builder.mutation({
      query: (params) => ({
        method: "POST",
        url: `/tenant/rules`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + params.token,
          slug: params.tenant_id,
        },
        body: params.body,
      }),
    }),
    updateRule: builder.mutation({
      query: (params) => ({
        method: "PUT",
        url: `/tenant/rules/${params.id}`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + params.token,
          slug: params.tenant_id,
        },
        body: params.body,
      }),
    }),
    updateRuleStatus: builder.mutation({
      query: (params) => ({
        method: "PATCH",
        url: `/tenant/rules/${params.id}/status`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + params.token,
          slug: params.tenant_id,
        },
        body: { status: params.status },
      }),
    }),
    validateRule: builder.mutation({
      query: (params) => ({
        method: "POST",
        url: `/tenant/rules/validate`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + params.token,
          slug: params.tenant_id,
        },
        body: params.body,
      }),
    }),
    getTrackingKeys: builder.mutation({
      query: (params) => ({
        method: "GET",
        url: `/tenant/rules/meta/tracking-keys`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + params.token,
          slug: params.tenant_id,
        },
      }),
    }),
    getActions: builder.mutation({
      query: (params) => ({
        method: "GET",
        url: `/tenant/rules/meta/actions`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + params.token,
          slug: params.tenant_id,
        },
      }),
    }),
    getRewardModes: builder.mutation({
      query: (params) => ({
        method: "GET",
        url: `/tenant/rules/meta/reward-modes`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + params.token,
          slug: params.tenant_id,
        },
      }),
    }),
    getRuleAnalyticsSummary: builder.mutation({
      query: (params) => ({
        method: "GET",
        url: `/tenant/rules/${params.id}/analytics/summary`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + params.token,
          slug: params.tenant_id,
        },
      }),
    }),
    getRuleAnalyticsUsers: builder.mutation({
      query: (params) => ({
        method: "GET",
        url: `/tenant/rules/${params.id}/analytics/users?page=${params.page || 1}&limit=${params.limit || 10}`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + params.token,
          slug: params.tenant_id,
        },
      }),
    }),
    getRuleAnalyticsTrend: builder.mutation({
      query: (params) => ({
        method: "GET",
        url: `/tenant/rules/${params.id}/analytics/trend?days=${params.days || 30}`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + params.token,
          slug: params.tenant_id,
        },
      }),
    }),

    getActiveSchemeWallets: builder.mutation({
      query: (params) => ({
        method: "GET",
        url: `/tenant/rules/scheme/wallets`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + params.token,
          slug: params.tenant_id,
        },
      }),
    }),

    // ---- Guided builder: capability matrix, wallets, catalog (queries) ----
    getMetaSchema: builder.query({
      query: (params) => ({
        url: `/tenant/rules/meta/schema`,
        headers: { Authorization: "Bearer " + params.token, slug: params.tenant_id },
      }),
      transformResponse: (r) => r?.body ?? r?.data ?? r,
    }),
    getWallets: builder.query({
      query: (params) => ({
        url: `/tenant/rules/scheme/wallets`,
        headers: { Authorization: "Bearer " + params.token, slug: params.tenant_id },
      }),
      transformResponse: (r) => r?.body ?? r?.data ?? r,
    }),
    getCatalogProducts: builder.query({
      query: (params) => ({
        url: `/tenant/rules/catalog/products?search=${encodeURIComponent(params.search || "")}&limit=${params.limit || 20}`,
        headers: { Authorization: "Bearer " + params.token, slug: params.tenant_id },
      }),
      transformResponse: (r) => r?.body ?? r?.data ?? r,
    }),
    getCatalogCategories: builder.query({
      query: (params) => ({
        url: `/tenant/rules/catalog/categories?search=${encodeURIComponent(params.search || "")}`,
        headers: { Authorization: "Bearer " + params.token, slug: params.tenant_id },
      }),
      transformResponse: (r) => r?.body ?? r?.data ?? r,
    }),
    getCatalogBrands: builder.query({
      query: (params) => ({
        url: `/tenant/rules/catalog/brands?search=${encodeURIComponent(params.search || "")}`,
        headers: { Authorization: "Bearer " + params.token, slug: params.tenant_id },
      }),
      transformResponse: (r) => r?.body ?? r?.data ?? r,
    }),
    validateCatalogValues: builder.mutation({
      query: (params) => ({
        method: "POST",
        url: `/tenant/rules/catalog/validate-values`,
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + params.token, slug: params.tenant_id },
        body: params.body,
      }),
    }),

    // ---- User types (tenant-wide) + scheme eligibility (for scheme-scoped rules) ----
    getRuleAppUserTypes: builder.query({
      query: (params) => ({
        url: `/app/appUserType`,
        headers: { Authorization: "Bearer " + params.token, slug: params.tenant_id },
      }),
      transformResponse: (r) => r?.body ?? r?.data ?? r,
    }),
    getSchemeFilters: builder.query({
      query: (params) => ({
        url: `/tenant/scheme/scheme-filters?scheme_id=${params.scheme_id}`,
        headers: { Authorization: "Bearer " + params.token, slug: params.tenant_id },
      }),
      transformResponse: (r) => r?.body ?? r?.data ?? r,
    }),
  }),
});

export const {
  useGetRulesMutation,
  useGetRuleByIdMutation,
  useCreateRuleMutation,
  useUpdateRuleMutation,
  useUpdateRuleStatusMutation,
  useValidateRuleMutation,
  useGetTrackingKeysMutation,
  useGetActionsMutation,
  useGetRewardModesMutation,
  useGetRuleAnalyticsSummaryMutation,
  useGetRuleAnalyticsUsersMutation,
  useGetRuleAnalyticsTrendMutation,
  useGetActiveSchemeWalletsMutation,
  // Guided builder
  useGetMetaSchemaQuery,
  useGetWalletsQuery,
  useLazyGetCatalogProductsQuery,
  useLazyGetCatalogCategoriesQuery,
  useLazyGetCatalogBrandsQuery,
  useValidateCatalogValuesMutation,
  useGetRuleAppUserTypesQuery,
  useLazyGetSchemeFiltersQuery,
} = rulesApi;
